import React, { useEffect, useMemo, useState } from 'react';
import { BarChart3, Loader2 } from 'lucide-react';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { cn } from '../../lib/utils';
import {
  PERFORMANCE_METRIC_OPTIONS,
  aggregateWorkflowEmailStats,
  extractEmailSteps,
  formatClickRateDisplay,
  formatMetricDisplay,
  formatOpenRateDisplay,
  getMetricOption,
  normalizeEmailStats,
  sortEmailRowsByMetric,
} from '../../utils/workflowStatisticsUtils';

const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');

async function fetchStepStats(stepId, token) {
  const res = await fetch(`${BACKEND_URL}/api/webhooks/mailgun/stats/${stepId}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) return null;
  return res.json();
}

const WorkflowStatisticsPanel = ({
  workflow,
  token,
  className,
}) => {
  const [selectedMetricId, setSelectedMetricId] = useState('open_rate');
  const [showNumbers, setShowNumbers] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const emailSteps = useMemo(
    () => extractEmailSteps(workflow?.steps || []),
    [workflow?.steps],
  );

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!emailSteps.length) {
        setRows([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const results = await Promise.all(
          emailSteps.map(async (step) => {
            try {
              const stats = await fetchStepStats(step.id, token);
              return normalizeEmailStats(step, stats || {});
            } catch {
              return normalizeEmailStats(step, {});
            }
          }),
        );
        if (!cancelled) setRows(results);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [emailSteps, token]);

  const selectedMetric = getMetricOption(selectedMetricId);
  const sortedRows = useMemo(
    () => sortEmailRowsByMetric(rows, selectedMetricId),
    [rows, selectedMetricId],
  );
  const totals = useMemo(() => aggregateWorkflowEmailStats(rows), [rows]);

  if (loading) {
    return (
      <div className={cn('flex items-center justify-center py-20', className)} data-testid="workflow-statistics-panel">
        <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
        <span className="ml-2 text-sm text-slate-500">Loading statistics...</span>
      </div>
    );
  }

  if (!emailSteps.length) {
    return (
      <div
        className={cn('mx-auto max-w-3xl rounded-xl border border-slate-200 bg-white p-10 text-center shadow-sm', className)}
        data-testid="workflow-statistics-panel"
      >
        <BarChart3 className="mx-auto mb-3 text-slate-400" size={40} />
        <p className="font-medium text-slate-800">No email actions yet</p>
        <p className="mt-1 text-sm text-slate-500">Add email steps to this workflow to view performance statistics.</p>
      </div>
    );
  }

  return (
    <div className={cn('mx-auto max-w-6xl space-y-6', className)} data-testid="workflow-statistics-panel">
      <div className="flex flex-wrap items-center justify-between gap-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="min-w-[220px]">
          <Label className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-slate-500">
            Performance metric
          </Label>
          <Select value={selectedMetricId} onValueChange={setSelectedMetricId}>
            <SelectTrigger className="w-[220px] bg-white" data-testid="workflow-statistics-metric-select">
              <SelectValue placeholder="Select metric" />
            </SelectTrigger>
            <SelectContent>
              {PERFORMANCE_METRIC_OPTIONS.map((option) => (
                <SelectItem key={option.id} value={option.id} data-testid={`workflow-statistics-metric-${option.id}`}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-3">
          <Switch
            id="workflow-statistics-show-numbers"
            checked={showNumbers}
            onCheckedChange={setShowNumbers}
            data-testid="workflow-statistics-show-numbers"
          />
          <Label htmlFor="workflow-statistics-show-numbers" className="text-sm text-slate-700">
            Show statistics in numbers
          </Label>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Open Rate</p>
          <p className="mt-2 text-2xl font-semibold text-slate-900" data-testid="workflow-statistics-open-rate">
            {formatOpenRateDisplay(totals, showNumbers)}
          </p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Click Rate</p>
          <p className="mt-2 text-2xl font-semibold text-slate-900" data-testid="workflow-statistics-click-rate">
            {formatClickRateDisplay(totals, showNumbers)}
          </p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Email sent</p>
          <p className="mt-2 text-2xl font-semibold text-slate-900">
            {formatMetricDisplay('email_sent', totals, true)}
          </p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Unsubscribed</p>
          <p className="mt-2 text-2xl font-semibold text-slate-900">
            {formatMetricDisplay('unsubscribed', totals, showNumbers)}
          </p>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-200 px-5 py-4">
          <h3 className="text-lg font-semibold text-slate-900">Top performing emails</h3>
          <p className="text-sm text-slate-500" data-testid="workflow-statistics-table-subtitle">
            List of top performing emails based on {selectedMetric.tableSubtitleMetric}
          </p>
        </div>

        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Email</TableHead>
                <TableHead>Open Rate</TableHead>
                <TableHead>Click Rate</TableHead>
                <TableHead>Email sent</TableHead>
                <TableHead>Unsubscribed</TableHead>
                <TableHead>Spam Complaints</TableHead>
                <TableHead>{selectedMetric.label}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="py-10 text-center text-sm text-slate-500">
                    No email statistics recorded yet.
                  </TableCell>
                </TableRow>
              ) : (
                sortedRows.map((row) => (
                  <TableRow key={row.id} data-testid={`workflow-statistics-email-row-${row.id}`}>
                    <TableCell>
                      <div>
                        <p className="font-medium text-slate-900">{row.name}</p>
                        <p className="text-xs text-slate-500">{row.subject}</p>
                      </div>
                    </TableCell>
                    <TableCell>{formatOpenRateDisplay(row, showNumbers)}</TableCell>
                    <TableCell>{formatClickRateDisplay(row, showNumbers)}</TableCell>
                    <TableCell>{formatMetricDisplay('email_sent', row, true)}</TableCell>
                    <TableCell>{formatMetricDisplay('unsubscribed', row, showNumbers)}</TableCell>
                    <TableCell>{formatMetricDisplay('spam_complaints', row, showNumbers)}</TableCell>
                    <TableCell className="font-medium text-slate-900">
                      {formatMetricDisplay(selectedMetricId, row, showNumbers)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
};

export default WorkflowStatisticsPanel;
